<template>
  <div class="min-h-screen bg-gray-50">
    <!-- Top Navigation Bar -->
    <nav class="bg-gray-900 border-b border-gray-700">
      <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div class="flex justify-between h-16">
          <!-- Left Side: Logo and Navigation -->
          <div class="flex">
            <!-- Logo -->
            <div class="flex-shrink-0 flex items-center">
              <ShieldCheckIcon class="h-8 w-8 text-red-500" />
              <span class="ml-2 text-white font-bold text-lg">Site Owner Panel</span>
            </div>

            <!-- Navigation Links -->
            <div class="hidden sm:ml-8 sm:flex sm:space-x-4">
              <Link
                href="/site-owner-administrator/dashboard"
                :class="[
                  'inline-flex items-center px-3 py-2 text-sm font-medium rounded-md',
                  $page.url === '/site-owner-administrator/dashboard'
                    ? 'bg-gray-800 text-white'
                    : 'text-gray-300 hover:bg-gray-800 hover:text-white'
                ]"
              >
                <HomeIcon class="h-5 w-5 mr-2" />
                Dashboard
              </Link>

              <Link
                href="/site-owner-administrator/companies"
                :class="[
                  'inline-flex items-center px-3 py-2 text-sm font-medium rounded-md',
                  $page.url.startsWith('/site-owner-administrator/companies')
                    ? 'bg-gray-800 text-white'
                    : 'text-gray-300 hover:bg-gray-800 hover:text-white'
                ]"
              >
                <BuildingOfficeIcon class="h-5 w-5 mr-2" />
                Companies
              </Link>

              <Link
                href="/site-owner-administrator/users"
                :class="[
                  'inline-flex items-center px-3 py-2 text-sm font-medium rounded-md',
                  $page.url.startsWith('/site-owner-administrator/users')
                    ? 'bg-gray-800 text-white'
                    : 'text-gray-300 hover:bg-gray-800 hover:text-white'
                ]"
              >
                <UsersIcon class="h-5 w-5 mr-2" />
                Users
              </Link>

              <Link
                href="/site-owner-administrator/subscriptions/plans"
                :class="[
                  'inline-flex items-center px-3 py-2 text-sm font-medium rounded-md',
                  $page.url.startsWith('/site-owner-administrator/subscriptions')
                    ? 'bg-gray-800 text-white'
                    : 'text-gray-300 hover:bg-gray-800 hover:text-white'
                ]"
              >
                <RectangleStackIcon class="h-5 w-5 mr-2" />
                Subscriptions
              </Link>

              <Link
                href="/site-owner-administrator/invoices"
                :class="[
                  'inline-flex items-center px-3 py-2 text-sm font-medium rounded-md',
                  $page.url.startsWith('/site-owner-administrator/invoices')
                    ? 'bg-gray-800 text-white'
                    : 'text-gray-300 hover:bg-gray-800 hover:text-white'
                ]"
              >
                <DocumentTextIcon class="h-5 w-5 mr-2" />
                Invoices
              </Link>

              <Link
                href="/site-owner-administrator/billing"
                :class="[
                  'inline-flex items-center px-3 py-2 text-sm font-medium rounded-md',
                  $page.url.startsWith('/site-owner-administrator/billing')
                    ? 'bg-gray-800 text-white'
                    : 'text-gray-300 hover:bg-gray-800 hover:text-white'
                ]"
              >
                <CurrencyDollarIcon class="h-5 w-5 mr-2" />
                Billing
              </Link>

              <Link
                href="/site-owner-administrator/system"
                :class="[
                  'inline-flex items-center px-3 py-2 text-sm font-medium rounded-md',
                  $page.url.startsWith('/site-owner-administrator/system')
                    ? 'bg-gray-800 text-white'
                    : 'text-gray-300 hover:bg-gray-800 hover:text-white'
                ]"
              >
                <Cog6ToothIcon class="h-5 w-5 mr-2" />
                System
              </Link>
            </div>
          </div>

          <!-- Right Side: User Menu -->
          <div class="flex items-center">
            <!-- User Dropdown -->
            <div class="ml-3 relative">
              <Menu as="div" class="relative inline-block text-left">
                <div>
                  <MenuButton class="flex items-center text-sm text-gray-300 hover:text-white focus:outline-none">
                    <div class="flex items-center">
                      <div class="h-8 w-8 rounded-full bg-gray-700 flex items-center justify-center">
                        <span class="text-white font-medium">
                          {{ $page.props.auth.user.first_name?.charAt(0) || '' }}{{ $page.props.auth.user.last_name?.charAt(0) || '' }}
                        </span>
                      </div>
                      <span class="ml-2">{{ $page.props.auth.user.first_name }} {{ $page.props.auth.user.last_name }}</span>
                      <ChevronDownIcon class="ml-1 h-4 w-4" />
                    </div>
                  </MenuButton>
                </div>

                <transition
                  enter-active-class="transition ease-out duration-100"
                  enter-from-class="transform opacity-0 scale-95"
                  enter-to-class="transform opacity-100 scale-100"
                  leave-active-class="transition ease-in duration-75"
                  leave-from-class="transform opacity-100 scale-100"
                  leave-to-class="transform opacity-0 scale-95"
                >
                  <MenuItems class="absolute right-0 mt-2 w-48 origin-top-right bg-white divide-y divide-gray-100 rounded-md shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
                    <div class="px-1 py-1">
                      <MenuItem v-slot="{ active }">
                        <Link
                          href="/site-owner-administrator/profile"
                          :class="[
                            active ? 'bg-gray-100' : '',
                            'group flex rounded-md items-center w-full px-2 py-2 text-sm text-gray-900'
                          ]"
                        >
                          <UserCircleIcon class="h-5 w-5 mr-2 text-gray-500" />
                          Profile
                        </Link>
                      </MenuItem>
                    </div>
                    <div class="px-1 py-1">
                      <MenuItem v-slot="{ active }">
                        <Link
                          href="/site-owner-administrator/logout"
                          method="post"
                          as="button"
                          :class="[
                            active ? 'bg-gray-100' : '',
                            'group flex rounded-md items-center w-full px-2 py-2 text-sm text-gray-900'
                          ]"
                        >
                          <ArrowRightOnRectangleIcon class="h-5 w-5 mr-2 text-gray-500" />
                          Logout
                        </Link>
                      </MenuItem>
                    </div>
                  </MenuItems>
                </transition>
              </Menu>
            </div>
          </div>
        </div>
      </div>
    </nav>

    <!-- Page Content -->
    <main class="py-10">
      <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <slot />
      </div>
    </main>

    <!-- Toast Notifications -->
    <Toast />
  </div>
</template>

<script setup>
import { Link } from '@inertiajs/vue3';
import { Menu, MenuButton, MenuItems, MenuItem } from '@headlessui/vue';
import {
  ShieldCheckIcon,
  HomeIcon,
  BuildingOfficeIcon,
  UsersIcon,
  RectangleStackIcon,
  DocumentTextIcon,
  CurrencyDollarIcon,
  Cog6ToothIcon,
  ChevronDownIcon,
  UserCircleIcon,
  ArrowRightOnRectangleIcon,
} from '@heroicons/vue/24/outline';
import Toast from '@/Components/Common/Toast.vue';
</script>
